import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faArrowLeft, faCheck } from '@fortawesome/free-solid-svg-icons';
import config from '../config';
import apiClient from '../utils/axiosInterceptor';
import { NewsletterInfo } from '../types/User';

interface NewsletterOnboardingProps {
    onComplete: (newsletterInfo: NewsletterInfo) => void;
    onSkip: () => void;
    existingData?: NewsletterInfo;
}

const TOTAL_STEPS = 2;

const NewsletterOnboarding: React.FC<NewsletterOnboardingProps> = ({ onComplete, onSkip, existingData }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [hasWorkedWithSponsors, setHasWorkedWithSponsors] = useState<boolean | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newsletterInfo, setNewsletterInfo] = useState<NewsletterInfo>(existingData || {
        name: '',
        topic: '',
        audience_size: 0,
        engagement_rate: 0,
        publishing_frequency: 'weekly',
        audience_demographics: {
            age_range: '26-35',
            income_range: '50-100K',
            location: 'US',
            interests: [],
            job_titles: []
        },
        sponsorship_history: {
            previous_sponsors: [],
            typical_rates: {
                newsletter_mention: 0,
                dedicated_email: 0,
                banner_ad: 0
            }
        },
        outreach_preferences: {
            style: 'professional',
            follow_up_frequency: 'once',
            minimum_deal_size: 0
        },
        sponsor_match_profile: {
            ideal_sponsor_categories: [],
            predicted_response_rate: 0,
            recommended_outreach_times: [],
            personalization_data_points: []
        },
        outreach_stats: {
            emails_sent: 0,
            responses_received: 0,
            deals_closed: 0,
            total_revenue: 0,
            average_response_rate: 0
        }
    });

    const topics = ['Technology', 'Finance', 'Marketing', 'Lifestyle', 'Health', 'Education', 'Business', 'Entertainment', 'Sports', 'Other'];

    const handleInputChange = (field: string, value: unknown) => {
        setNewsletterInfo(prev => {
            if (field.includes('.')) {
                const parts = field.split('.');
                if (parts.length === 2) {
                    const [parent, child] = parts;
                    const parentValue = prev[parent as keyof NewsletterInfo] as Record<string, unknown>;
                    return {
                        ...prev,
                        [parent]: {
                            ...parentValue,
                            [child]: value
                        }
                    };
                }
            }
            return {
                ...prev,
                [field]: value
            };
        });
    };

    const handleSponsorExperienceToggle = (value: boolean) => {
        setHasWorkedWithSponsors(value);
        setNewsletterInfo(prev => ({
            ...prev,
            sponsorship_history: {
                ...prev.sponsorship_history,
                has_worked_with_sponsors: value,
                previous_sponsors: value ? prev.sponsorship_history?.previous_sponsors || [] : []
            }
        }));
    };

    const nextStep = async () => {
        if (currentStep < TOTAL_STEPS) {
            setCurrentStep(currentStep + 1);
        } else {
            await handleSubmit();
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

            if (!isLocalDev) {
                await apiClient.put(`${config.backendUrl}users/newsletter-info`, newsletterInfo);
            } else {
                localStorage.setItem('dev_newsletter_info', JSON.stringify(newsletterInfo));
            }

            onComplete(newsletterInfo);
        } catch (error) {
            console.error('❌ Error saving newsletter info:', error);
            onComplete(newsletterInfo);
        } finally {
            setIsSubmitting(false);
        }
    };

    const prevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };

    const renderStep1 = () => (
        <div className="onboarding-step">
            <div className="onboarding-header">
                <h2>Tell us about your newsletter</h2>
                <p>Help us match you with the perfect sponsors</p>
            </div>

            <div className="onboarding-form onboarding-form--centered">
                <div className="form-group full-width">
                    <label>Newsletter Name *</label>
                    <input
                        type="text"
                        value={newsletterInfo.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="e.g., Tech Weekly"
                        required
                    />
                </div>

                <div className="form-group">
                    <label>Primary Topic *</label>
                    <select
                        value={newsletterInfo.topic}
                        onChange={(e) => handleInputChange('topic', e.target.value)}
                        required
                    >
                        <option value="">Select a topic</option>
                        {topics.map(topic => (
                            <option key={topic} value={topic}>{topic}</option>
                        ))}
                    </select>
                </div>

                <div className="form-group">
                    <label>Audience Size *</label>
                    <select
                        value={newsletterInfo.audience_size}
                        onChange={(e) => handleInputChange('audience_size', parseInt(e.target.value))}
                        required
                    >
                        <option value={0}>Select audience size</option>
                        <option value={1000}>1K – 5K subscribers</option>
                        <option value={5000}>5K – 25K subscribers</option>
                        <option value={25000}>25K – 50K subscribers</option>
                        <option value={50000}>50K – 100K subscribers</option>
                        <option value={100}>Under 1K (growing)</option>
                        <option value={100000}>100K+ subscribers</option>
                    </select>
                </div>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="onboarding-step">
            <div className="onboarding-header">
                <h2>Almost done</h2>
                <p>One last question and you&apos;re ready to find sponsors</p>
            </div>

            <div className="onboarding-form onboarding-form--centered onboarding-form--single-column">
                <div className="form-group full-width">
                    <label>Have you worked with sponsors before?</label>
                    <p className="form-group__hint">Helps us tailor your auto-drafted pitches</p>
                    <div className="toggle-group">
                        <button
                            type="button"
                            className={`toggle-btn ${hasWorkedWithSponsors === true ? 'toggle-btn--active' : ''}`}
                            onClick={() => handleSponsorExperienceToggle(true)}
                        >
                            Yes
                        </button>
                        <button
                            type="button"
                            className={`toggle-btn ${hasWorkedWithSponsors === false ? 'toggle-btn--active' : ''}`}
                            onClick={() => handleSponsorExperienceToggle(false)}
                        >
                            No
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderProgressBar = () => (
        <div className="onboarding-progress">
            <div className="progress-bar">
                <div
                    className="progress-fill"
                    style={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }}
                />
            </div>
            <span className="progress-text">Step {currentStep} of {TOTAL_STEPS}</span>
        </div>
    );

    return (
        <div className="newsletter-onboarding">
            {renderProgressBar()}

            <div className="onboarding-step-content">
                {currentStep === 1 && renderStep1()}
                {currentStep === 2 && renderStep2()}
            </div>

            <div className="onboarding-actions">
                {currentStep > 1 ? (
                    <button
                        type="button"
                        className="btn btn-secondary onboarding-nav-btn"
                        onClick={prevStep}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} /> Back
                    </button>
                ) : (
                    <span className="onboarding-actions-spacer" />
                )}

                <div className="onboarding-actions-right">
                    <button
                        type="button"
                        className="btn btn-skip onboarding-nav-btn"
                        onClick={onSkip}
                    >
                        Skip for now
                    </button>

                    <button
                        type="button"
                        className="btn btn-primary onboarding-nav-btn"
                        onClick={nextStep}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Saving...' : (currentStep === TOTAL_STEPS ? 'Complete Setup' : 'Next')}
                        {currentStep < TOTAL_STEPS && !isSubmitting && <FontAwesomeIcon icon={faArrowRight} />}
                        {currentStep === TOTAL_STEPS && !isSubmitting && <FontAwesomeIcon icon={faCheck} />}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NewsletterOnboarding;
